import InquiryApi from '@apis/inquiry/InquiryApi';
import useToast from '@hooks/ui/useToast';
import { ErrorResponse } from '@models/error';
import { CreateInquiryRequest } from '@models/inquiry';
import { useFlow, useStepFlow } from '@stackflow/react';
import { useMutation } from '@tanstack/react-query';
import { containsProfanity } from '@utils/profanityUtils';
import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inquiryTypeIdAtom } from '../../../atoms/inquiryFormAtoms';

function useInquiryForm() {
    const { t } = useTranslation('inquiry');
    const [inquiryTypeId, setInquiryTypeId] = useAtom(inquiryTypeIdAtom);
    const [title, setTitle] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [fileUrls, setFileUrls] = useState<string[]>([]);
    const toast = useToast();
    const [privacy, setPrivacy] = useState<boolean>(false);
    // 유형·제목·내용 미입력 오류는 토스트 대신 각 입력 필드 아래에 표시한다
    const [typeError, setTypeError] = useState<string>('');
    const [titleError, setTitleError] = useState<string>('');
    const [contentError, setContentError] = useState<string>('');
    // 금칙어는 입력 즉시 검사해 필수 입력 오류와 같은 자리에 보여준다
    const titleProfanityError = containsProfanity(title) ? t('form.profanity') : '';
    const contentProfanityError = containsProfanity(content) ? t('form.profanity') : '';
    const { replaceStep } = useStepFlow('InquiryActivity');
    const { push } = useFlow();

    // 폼을 벗어나면 선택한 유형이 다음 진입에 남지 않도록 비운다
    useEffect(() => () => setInquiryTypeId(''), []);

    // 값을 입력하면 오류를 지운다 (유형 선택으로 내용이 채워지는 경우 포함)
    useEffect(() => {
        if (inquiryTypeId) setTypeError('');
    }, [inquiryTypeId]);
    useEffect(() => {
        if (title) setTitleError('');
    }, [title]);
    useEffect(() => {
        if (content) setContentError('');
    }, [content]);

    const reset = () => {
        setInquiryTypeId('');
        setTitle('');
        setContent('');
        setFileUrls([]);
        setPrivacy(false);
        setTypeError('');
        setTitleError('');
        setContentError('');
    };

    const { mutate } = useMutation({
        mutationFn: InquiryApi.createInquiry,
        onSuccess(data) {
            toast.success(t('toast.success'));
            reset();
            const { inquiryId } = data;
            replaceStep({ type: 'READ' });
            push('InquiryLogActivity', { inquiryId });
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('toast.error'));
        },
    });

    const handleCreateInquiry = () => {
        const nextTypeError = inquiryTypeId ? '' : t('toast.typeRequired');
        const nextTitleError = title ? '' : t('toast.titleRequired');
        const nextContentError = content ? '' : t('toast.contentRequired');
        setTypeError(nextTypeError);
        setTitleError(nextTitleError);
        setContentError(nextContentError);
        if (nextTypeError || nextTitleError || nextContentError) {
            return;
        }
        if (titleProfanityError || contentProfanityError) {
            return;
        }
        if (!privacy) {
            toast.error(t('toast.privacyRequired'));
            return;
        }
        const request: CreateInquiryRequest = {
            inquiryTypeId, title, content, fileUrls,
        };
        mutate(request);
    };
    return {
        inquiryTypeId,
        setInquiryTypeId,
        title,
        setTitle,
        content,
        setContent,
        fileUrls,
        setFileUrls,
        handleCreateInquiry,
        reset,
        privacy,
        setPrivacy,
        typeError,
        titleError: titleError || titleProfanityError,
        contentError: contentError || contentProfanityError,
    };
}

export default useInquiryForm;
